import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './styles/global.css';
import Header from './components/Header';
import MobileTabBar from './components/MobileTabBar';
import MobileDrawer from './components/MobileDrawer';
import { MapModalProvider } from './components/MapModal';
import Home from './pages/Home';

const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Coverage = lazy(() => import('./pages/Coverage'));
const Faq = lazy(() => import('./pages/Faq'));
const Distribute = lazy(() => import('./pages/Distribute'));
const Legal = lazy(() => import('./pages/Legal'));

function NotFound() {
  return (
    <main className="page-section active pg" style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', padding: '120px 24px 80px', textAlign: 'center' }}>
      <div>
        <p style={{ color: '#00A99D', fontWeight: 800, letterSpacing: '.12em' }}>404</p>
        <h1 style={{ color: '#102A43', fontSize: 'clamp(32px, 6vw, 52px)', margin: '8px 0 12px' }}>Page not found</h1>
        <p style={{ color: '#52606D', marginBottom: 24 }}>The page you are looking for may have moved or no longer exists.</p>
        <a className="btn btn-t" href="/">Back to home</a>
      </div>
    </main>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/* Header hide-on-scroll — port of the scroll listener that toggles `up` on #hdr */
function useHeaderHide() {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let prev = 0;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const y = window.scrollY;
          setHidden(y > prev && y > 110);
          prev = y;
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return hidden;
}

function Mush() {
  const navigate = useNavigate();
  return (
    <button className="mush" onClick={() => navigate('/distribute')} title="Hire Us">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
      <span>Hire Us</span>
    </button>
  );
}

function Shell() {
  const hidden = useHeaderHide();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
  }, [drawerOpen]);

  return (
    <MapModalProvider>
      <ScrollToTop />
      <Header hidden={hidden} onToggleDrawer={() => setDrawerOpen((o) => !o)} />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/coverage" element={<Coverage />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/distribute" element={<Distribute />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <MobileTabBar />

      {/* WhatsApp FAB */}
      <a href="https://wa.me/919319315523" className="wa-btn" target="_blank" rel="noopener noreferrer" title="WhatsApp Us"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="24" height="24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" /></svg></a>
      <Mush />
    </MapModalProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
